/**
 * @license Copyright (c) 2022, Memo.ph by Globe Telecom. All rights reserved.
 */

import { useLocation } from '@reach/router';
import firebase from 'firebase/app';
import 'firebase/functions';
import _ from 'lodash';
import React from 'react';
import loadError from '../../../assets/img/load_error.svg';
import emptyMemo from '../../../assets/img/memo-empty-state.svg';
import Button from '../../../layouts/buttons/Button';
import Preloader from '../../../layouts/preloaders/Preloader';
import Table, { TableImgStyle } from '../../../layouts/table/Table';
import TableBody from '../../../layouts/table/TableBody';
import TableBodyCell from '../../../layouts/table/TableBodyCell';
import TableHeader from '../../../layouts/table/TableHeader';
import TableHeaderCell from '../../../layouts/table/TableHeaderCell';
import TablePagination from '../../../layouts/table/TablePagination';
import TableRow from '../../../layouts/table/TableRow';
import Text from '../../../layouts/typography/Text';
import { Memo } from '../../../models/Memo';
import { getDateString } from '../../../utils/date/Date';

export interface QueryParams {
    limit: number;
    page_no: number;
    start_date: Date | null;
    end_date: Date | null;
    memo_type: string;
}

export interface ITableHeader {
    label: string;
    key: string;
}

export interface DatatableComponentProps {
    headers?: ITableHeader[];
    memos?: Memo[];
}

export interface DatatableProps {
    config: {
        endpoint: string;
        templatePath: string;
        queryParams: QueryParams;
    };
    headerComponent?: JSX.Element;
    bodyComponent?: JSX.Element;
    emptyStateComponent?: JSX.Element;
    pageRedirectTo?: string;
    defaultHeaders: ITableHeader[];
    deps?: any;
}
// <Datatable endpoint="" headerPaths="" />
const Datatable = (props: DatatableProps) => {
    const { memo_type, start_date, end_date } = props.config.queryParams;
    const [headers, setHeaders] = React.useState<ITableHeader[]>(props.defaultHeaders);
    const [memos, setMemos] = React.useState<Memo[]>([]);
    const [requestState, setRequestState] = React.useState('idle');
    const [requestErrorMessage, setRequestErrorMessage] = React.useState('');

    const location = useLocation();

    const updateTableHeaders = async () => {
        try {
            const getTemplateById = await firebase.app().functions('asia-east2').httpsCallable('get_template_by_id');
            const template = await getTemplateById({ template_id: memo_type });
            if (template.data.headers) {
                const headers = _.get(template.data.headers, props.config.templatePath);
                if (headers) {
                    setHeaders(headers);
                }
            } else {
                setHeaders(props.defaultHeaders);
            }
        } catch (err) {
            throw err;
        }
    };

    const getMemos = async (pageQuery: any) => {
        try {
            let requestPayload: any = {
                limit: Number(pageQuery.limit),
                start_after: pageQuery.startAfter,
                end_before: pageQuery.endBefore,
                limit_to_last: pageQuery.limitToLast,
            };

            if (start_date && end_date) {
                requestPayload = {
                    ...requestPayload,
                    start_date: start_date.toISOString(),
                    end_date: end_date.toISOString(),
                };
            }

            if (memo_type && memo_type !== 'ALL') {
                await updateTableHeaders();
                requestPayload = {
                    ...requestPayload,
                    memo_type: memo_type,
                };
            } else {
                setHeaders(props.defaultHeaders);
            }

            const getFn = firebase.app().functions('asia-east2').httpsCallable(props.config.endpoint);

            const httpResponse = await getFn(requestPayload);
            let data = [];

            if (httpResponse.data.memos && httpResponse.data.memos.length) {
                data = httpResponse.data.memos.map((memo: Memo) => {
                    return {
                        ...memo,
                        find: function (key: string) {
                            return _(this.memo_content)
                                .thru((content) => _.union(content, _.map(content, 'value') || []))
                                .flatten()
                                .find((field) => field.field_id === key || field.field_name === key);
                        },
                    };
                });
            } else {
                data = memos;
            }
            return {
                page_info: httpResponse.data.page_info,
                memos: data,
            };
        } catch (err) {
            throw err;
        }
    };

    React.useEffect(() => {
        setMemos([]);

        const search = new URLSearchParams(location.search);
        const limit = search.get('limit');
        const startAfter = search.get('startAfter');
        const endBefore = search.get('endBefore');
        const limitToLast = search.get('q') === 'oldest' || (search.get('endBefore') && true);

        let isMounted = true;
        const loadMemos = async () => {
            try {
                setRequestState('pending');

                const pageQuery = {
                    limit: limit || '',
                    startAfter: startAfter || null,
                    endBefore: endBefore || null,
                    limitToLast: limitToLast,
                };

                const { memos } = await getMemos(pageQuery);
                if (isMounted) {
                    setMemos(memos);
                    setRequestState('fulfilled');
                }
            } catch (err) {
                setRequestState('rejected');
                console.error(err);
                setRequestErrorMessage(JSON.stringify(err));
                setMemos([]);
            }
        };
        loadMemos();
        return () => {
            isMounted = false;
        };
    }, [memo_type, start_date, end_date, location, props.deps]);

    const renderTableHeaders = () => {
        if (!props.headerComponent) {
            return (
                <TableRow type="header">
                    {headers.map((header) => (
                        <TableHeaderCell key={header.key}>{header.label}</TableHeaderCell>
                    ))}
                </TableRow>
            );
        }

        return React.cloneElement(props.headerComponent, { headers: headers, memos: memos });
    };

    const renderTableBody = () => {
        if (!props.bodyComponent) {
            return memos.map((_memo) => {
                const memo = (_memo as unknown) as Record<string, string>;
                return (
                    <TableRow key={_memo.memo_id}>
                        {headers.map((header) => {
                            // Parse date values included in the data.
                            if (['date_submitted', 'date_last_modified', 'date_created'].includes(header.key)) {
                                return (
                                    <TableBodyCell key={header.key}>{getDateString(memo[header.key])}</TableBodyCell>
                                );
                            }

                            // Render the data as is.
                            return <TableBodyCell key={memo[header.key]}>{memo[header.key]}</TableBodyCell>;
                        })}
                    </TableRow>
                );
            });
        }

        return React.cloneElement(props.bodyComponent, { headers: headers, memos: memos });
    };

    return (
        <>
            <Table>
                <TableHeader>{renderTableHeaders()}</TableHeader>
                <TableBody>
                    {renderTableBody()}
                    {requestState === 'pending' && (
                        <tr>
                            <td colSpan={headers.length}>
                                <div className="w-full mx-auto">
                                    <Preloader type="dots" />
                                </div>
                            </td>
                        </tr>
                    )}
                </TableBody>
            </Table>
            {requestState === 'fulfilled' && memos.length === 0 && (
                <div className="w-full py-8 flex flex-col justify-center items-center text-center">
                    <img src={emptyMemo} style={TableImgStyle} />
                    <div className="mt-8">
                        <Text>No memos to show.</Text>
                    </div>
                    {props.emptyStateComponent}
                </div>
            )}
            {requestState === 'rejected' && (
                <div className="w-full py-8 flex flex-col justify-center items-center text-center">
                    <img src={loadError} style={TableImgStyle} alt="Request Error" />
                    <div className="mt-8">
                        <Text>Something went wrong in fetching data</Text>
                        <Text>{requestErrorMessage}</Text>
                    </div>
                    <div className="mt-2 p-2">
                        <Button color="primary" onClick={() => window.location.reload()}>
                            Try again
                        </Button>
                    </div>
                </div>
            )}
            <TablePagination
                baseHref={props.pageRedirectTo as string}
                startAfter={memos[memos.length - 1] ? memos[memos.length - 1].memo_id : ''}
                endBefore={memos[0] ? memos[0].memo_id : ''}
            />
        </>
    );
};

export default Datatable;
